import React from 'react';
import { Page } from '../../../types';
import { PreviewHorizontalSlide } from './PreviewHorizontalSlide';

interface PagePreviewProps {
  page: Page | null;
  codeContent: string;
  previewType?: 'html' | 'settings';
  ctaButtonVisible?: boolean;
  ctaButtonId?: string; // 後方互換性のため残す
  ctaButtonIds?: string[]; // 複数ボタン表示用
  companyInfoVisible?: boolean;
  editState?: any;
  horizontalSlideData?: Partial<Page>; // 横スライドデータ（リアクティブ更新用）
}

export const PagePreview: React.FC<PagePreviewProps> = ({
  page,
  codeContent,
  previewType = 'html',
  ctaButtonVisible = true,
  ctaButtonId,
  ctaButtonIds = [],
  companyInfoVisible = true,
  editState,
  horizontalSlideData
}) => {
  // 選択されたCTAボタンの配列を取得
  const selectedCtaButtons = React.useMemo(() => {
    if (!editState?.settings?.ctaButtons) return [];
    
    let buttons: any[] = [];
    
    // 新しい方式（ctaButtonIds）が設定されている場合
    if (ctaButtonIds.length > 0) {
      buttons = editState.settings.ctaButtons.filter((btn: any) => 
        ctaButtonIds.includes(btn.id)
      );
    }
    // 後方互換性：ctaButtonIdが設定されている場合
    else if (ctaButtonId) {
      const button = editState.settings.ctaButtons.find((btn: any) => btn.id === ctaButtonId);
      buttons = button ? [button] : [];
    }
    
    // 固定表示モードのボタンのみをフィルタリング
    // 埋め込み式ボタンはHTML/CSS設定内で直接配置されるため、ここでは表示しない
    return buttons.filter((btn: any) => !btn.displayMode || btn.displayMode === 'fixed');
  }, [ctaButtonId, ctaButtonIds, editState?.settings?.ctaButtons]);
  const iframeRef = React.useRef<HTMLIFrameElement | null>(null);

  // 埋め込み式CTAボタンの配列を取得
  const embeddedCtaButtons = React.useMemo(() => {
    if (!editState?.settings?.ctaButtons) return [];
    
    let buttons: any[] = [];
    
    // 選択されたCTAボタンを取得
    if (ctaButtonIds.length > 0) {
      buttons = editState.settings.ctaButtons.filter((btn: any) => 
        ctaButtonIds.includes(btn.id)
      );
    } else if (ctaButtonId) {
      const button = editState.settings.ctaButtons.find((btn: any) => btn.id === ctaButtonId);
      buttons = button ? [button] : [];
    }
    
    // 埋め込み式ボタンのみをフィルタリング
    return buttons.filter((btn: any) => btn.displayMode === 'embedded');
  }, [ctaButtonId, ctaButtonIds, editState?.settings?.ctaButtons]);

  // iframeへプレビュー描画
  const renderIframePreview = React.useCallback(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;
    const doc = iframe.contentDocument;
    if (!doc) return;

    const raw = codeContent || '';
    const styleMatch = raw.match(/<style[^>]*>([\s\S]*?)<\/style>/i);
    const userCSS = styleMatch?.[1] || '';
    const userHTML = raw.replace(/<style[^>]*>[\s\S]*?<\/style>/gi, '');

    const bgImage = page?.backgroundImage;

    const baseCSS = `
      html, body { margin: 0; padding: 0; height: 100%; }
      #root { position: relative; width: 100%; height: 100%; overflow: hidden; }
    `;

    const bgStyle = bgImage
      ? `background-image: url('${bgImage}'); background-size: cover; background-position: center; background-repeat: no-repeat;`
      : `background: #f0f0f0;`;

    // 埋め込み式CTAボタンのHTMLを生成
    const embeddedCtaHTML = embeddedCtaButtons.map((btn: any, index: number) => {
      // 位置計算
      let leftStyle: string = '50%';
      let transformStyle: string = 'translateX(-50%)';
      
      switch (btn.leftPosition) {
        case 'center':
          leftStyle = '50%';
          transformStyle = 'translateX(-50%)';
          break;
        case 'left':
          leftStyle = '0';
          transformStyle = 'none';
          break;
        case 'right':
          leftStyle = '100%';
          transformStyle = 'translateX(-100%)';
          break;
        case 'custom':
          leftStyle = `${btn.leftPercentCustom ?? 0}%`;
          transformStyle = 'none';
          break;
      }
      
      const imgTag = btn.image 
        ? `<img src="${btn.image}" alt="${btn.name}" style="width: 100%; height: auto; display: block;" />` 
        : '';
      
      return `
        <div class="embedded-cta-button" data-cta-id="${btn.id}" style="
          position: absolute;
          bottom: ${btn.bottomPercent}%;
          left: ${leftStyle};
          transform: ${transformStyle};
          width: ${btn.widthPercent ?? 80}%;
          z-index: 500;
          cursor: pointer;
        ">
          ${imgTag}
        </div>
      `;
    }).join('');

    doc.open();
    doc.write(`<!DOCTYPE html><html><head><meta charset="utf-8">\n<style>${baseCSS}</style>\n<style>${userCSS}</style></head><body>\n<div id="root" style="${bgStyle}">\n  <div class="qwipe-preview-scope">${userHTML}</div>\n  ${embeddedCtaHTML}\n</div>\n</body></html>`);
    doc.close();
  }, [codeContent, embeddedCtaButtons]);

  React.useEffect(() => {
    if (previewType === 'html') {
      const timer = setTimeout(() => {
    renderIframePreview();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [previewType, codeContent, renderIframePreview]);

  // iframe読み込み完了時にプレビューを描画
  React.useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe || previewType !== 'html') return;

    const handleLoad = () => {
      renderIframePreview();
    };

    iframe.addEventListener('load', handleLoad);
    return () => {
      iframe.removeEventListener('load', handleLoad);
    };
  }, [previewType, renderIframePreview]);

  // その他設定のプレビュー
  // 横スライドが存在する場合はPreviewHorizontalSlideコンポーネントを使用
  // horizontalSlideDataがある場合はそれを優先（リアクティブ更新用）
  const horizontalSlides = (horizontalSlideData && 'horizontalSlides' in horizontalSlideData) 
    ? horizontalSlideData.horizontalSlides 
    : page?.horizontalSlides;
  const hasHorizontalSlides = horizontalSlides && Array.isArray(horizontalSlides) && horizontalSlides.length > 0;
  
  // 初期表示スライド番号を取得（1始まり、入力値優先）
  const initialIndex = (horizontalSlideData && 'horizontalSlideInitialIndex' in horizontalSlideData)
    ? (horizontalSlideData.horizontalSlideInitialIndex ?? 1)
    : (page?.horizontalSlideInitialIndex ?? 1);
  
  // スライド幅を取得（入力値優先）
  const slideWidth = (horizontalSlideData && 'horizontalSlideWidth' in horizontalSlideData)
    ? (horizontalSlideData.horizontalSlideWidth ?? 100)
    : (page?.horizontalSlideWidth ?? 100);
  
  const viewportWidth = (horizontalSlideData && 'horizontalSlideViewportWidth' in horizontalSlideData)
    ? (horizontalSlideData.horizontalSlideViewportWidth ?? 100)
    : (page?.horizontalSlideViewportWidth ?? 100);
  
  const positionAdjustment = (horizontalSlideData && 'horizontalSlidePositionAdjustment' in horizontalSlideData)
    ? (horizontalSlideData.horizontalSlidePositionAdjustment ?? 0)
    : (page?.horizontalSlidePositionAdjustment ?? 0);
  
  const indicatorPosition = (horizontalSlideData && 'horizontalSlideIndicatorPosition' in horizontalSlideData)
    ? (horizontalSlideData.horizontalSlideIndicatorPosition ?? 'bottom')
    : (page?.horizontalSlideIndicatorPosition || 'bottom');
  
  const indicatorOffsetX = (horizontalSlideData && 'horizontalSlideIndicatorOffsetX' in horizontalSlideData)
    ? (horizontalSlideData.horizontalSlideIndicatorOffsetX ?? 0)
    : (page?.horizontalSlideIndicatorOffsetX ?? 0);
  
  const indicatorOffsetY = (horizontalSlideData && 'horizontalSlideIndicatorOffsetY' in horizontalSlideData)
    ? (horizontalSlideData.horizontalSlideIndicatorOffsetY ?? 0)
    : (page?.horizontalSlideIndicatorOffsetY ?? 0);
  
  const infiniteLoop = (horizontalSlideData && 'horizontalSlideInfiniteLoop' in horizontalSlideData)
    ? (horizontalSlideData.horizontalSlideInfiniteLoop ?? true)
    : (page?.horizontalSlideInfiniteLoop !== false);
  
  return (
    <div style={{ height: '468px', width: '282px', padding: '16px' }}>
      <div style={{
        width: '100%',
        height: '100%',
        border: '1px solid #e0e0e0',
        borderRadius: 8,
        position: 'relative',
        overflow: 'hidden',
        backgroundColor: 'white'
      }}>
        {previewType === 'html' ? (
          <>
            <iframe
              ref={iframeRef}
              title="Page Preview"
              style={{ 
                width: '100%', 
                height: '100%', 
                border: 'none',
                borderRadius: 8,
                position: 'absolute',
                top: 0,
                left: 0,
                zIndex: 0
              }}
              onLoad={() => {
                renderIframePreview();
              }}
            />
            {hasHorizontalSlides && (
              <div style={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: '100%',
                height: 'auto',
                zIndex: 1,
                overflow: 'visible',
                pointerEvents: 'auto'
              }}>
                <PreviewHorizontalSlide
                  slides={horizontalSlides}
                  className="preview-horizontal-slide-main"
                  slideWidth={slideWidth}
                  viewportWidth={viewportWidth}
                  indicatorPosition={indicatorPosition}
                  infiniteLoop={infiniteLoop}
                  initialIndex={initialIndex}
                  positionAdjustment={positionAdjustment}
                  indicatorOffsetX={indicatorOffsetX}
                  indicatorOffsetY={indicatorOffsetY}
                  pageId={page?.id}
                />
              </div>
            )}
          </>
        ) : (
          <>
        {page?.backgroundImage && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${page.backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            zIndex: 0
          }} />
        )}
        
            {hasHorizontalSlides && (
          <div style={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
                width: '100%',
            height: 'auto',
            zIndex: 1,
            overflow: 'visible',
            pointerEvents: 'auto'
          }}>
            <PreviewHorizontalSlide
              slides={horizontalSlides}
              className="preview-horizontal-slide-main"
              slideWidth={slideWidth}
              viewportWidth={viewportWidth}
              indicatorPosition={indicatorPosition}
              infiniteLoop={infiniteLoop}
              initialIndex={initialIndex}
              positionAdjustment={positionAdjustment}
              indicatorOffsetX={indicatorOffsetX}
              indicatorOffsetY={indicatorOffsetY}
              pageId={page?.id}
            />
          </div>
        )}

        {/* CTAボタン（複数表示対応） */}
        {ctaButtonVisible && selectedCtaButtons.map((selectedCtaButton: any, index: number) => {
          // 位置計算（デフォルト値を設定）
          let leftStyle: string = '50%';
          let transformStyle: string = 'translateX(-50%)';
          
          if (selectedCtaButton.leftPosition) {
            switch (selectedCtaButton.leftPosition) {
              case 'center':
                leftStyle = '50%';
                transformStyle = 'translateX(-50%)';
                break;
              case 'left':
                leftStyle = '0';
                transformStyle = 'none';
                break;
              case 'right':
                leftStyle = '100%';
                transformStyle = 'translateX(-100%)';
                break;
              case 'custom':
                leftStyle = `${selectedCtaButton.leftPercentCustom ?? 0}%`;
                transformStyle = 'none';
                break;
            }
          } else if (selectedCtaButton.leftPercent !== undefined) {
            // 後方互換性
            leftStyle = `${selectedCtaButton.leftPercent}%`;
            transformStyle = 'translateX(-50%)';
          }
          
          // アニメーションクラス名を取得
          const getAnimationClass = () => {
            const animationType = selectedCtaButton.animationType || 'none';
            if (animationType === 'none') return '';
            return `qwipe-cta-animation-${animationType}`;
          };
          
          return (
            <div 
              key={`cta-${selectedCtaButton.id || index}`}
              style={{
                position: 'absolute',
                bottom: companyInfoVisible && editState?.settings?.companyInfo && 
                        (editState.settings.companyInfo.companyName || editState.settings.companyInfo.description || 
                         editState.settings.companyInfo.address || editState.settings.companyInfo.website)
                        ? '40px' : `${selectedCtaButton.bottomPercent}%`,
                left: leftStyle,
                transform: transformStyle,
                width: `${selectedCtaButton.widthPercent ?? 80}%`,
                zIndex: 20 + index,
                transition: 'bottom 0.3s ease'
              }}
            >
              <div className={getAnimationClass()}>
                {selectedCtaButton.image ? (
                  <img
                    src={selectedCtaButton.image}
                    alt="CTAボタン"
                    style={{
                      width: '100%',
                      height: 'auto',
                      display: 'block',
                      borderRadius: '6px',
                      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)'
                    }}
                  />
                ) : (
                  <div style={{
                    width: '100%',
                    height: 'auto',
                    backgroundColor: '#1976d2',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
                    padding: '8px 16px'
                  }}>
                    {selectedCtaButton.name}
                  </div>
                )}
              </div>
            </div>
          );
        })}

        {/* 会社概要の帯 */}
        {companyInfoVisible && editState?.settings?.companyInfo && 
         (editState.settings.companyInfo.companyName || editState.settings.companyInfo.description || 
          editState.settings.companyInfo.address || editState.settings.companyInfo.website) && (
          <div style={{
            position: 'absolute',
            bottom: '0',
            left: '50%',
            transform: 'translateX(-50%)',
            backgroundColor: '#000',
            color: 'white',
            padding: '8px 0',
            textAlign: 'center',
            fontSize: '14px',
            zIndex: 10,
            borderRadius: '0 0 12px 12px',
            width: '100%'
          }}>
            会社概要
          </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

